<template>
  <div class="product-review-slider">
    <!--
      Swiper 本体は支給の `.slick-list` にあたる内側の要素にする。
      `.swiper` は `overflow: hidden` を持つので、これを `.product-review-slider` と
      同じ要素にすると **枠の外に置く矢印（PC は `left: -30px`）が切り落とされる**。
    -->
    <div ref="swiperEl" class="swiper">
      <div class="swiper-wrapper">
        <div v-for="(slide, slideIndex) in slides" :key="slideIndex" class="product-review-slide swiper-slide">
          <div v-for="(card, cardIndex) in slide" :key="cardIndex" class="product-review-card">
            <p class="product-review-card_title">{{ card.title }}</p>
            <p class="product-review-card_text">{{ card.text }}</p>
            <p class="product-review-card_user" :class="card.gender">
              <img :src="`/image/icon_user-${card.gender}.png`" alt="" class="product-review-card_icon" />{{ card.meta }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- 矢印とドットは枠の外側。1ページしかない商品では出さない -->
    <template v-if="slides.length > 1">
      <button
        ref="prevEl"
        type="button"
        class="product-review-arrow product-review-arrow_prev"
        aria-label="前へ"
      ></button>
      <button
        ref="nextEl"
        type="button"
        class="product-review-arrow product-review-arrow_next"
        aria-label="次へ"
      ></button>
      <div ref="paginationEl" class="swiper-pagination product-review-dots"></div>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-review-slider`（ご愛用者様レビュー）。**9/38**
 * （`501` `510` `512` `513` `516` `525` `558` `559` `810`。既存 `productContents/<商品コード>/voice.vue`
 * を持つ9商品と完全一致）。
 *
 * **1ページあたりのカード数は PC 4枚・SP 2枚**（支給 `script_main.js:294` の
 * `perSlide = isSp ? 2 : 4`。`isSp` は 768px 以下）。支給はブレークポイントを跨ぐたびに
 * `unslick` して DOM を作り直すが、**こちらは配列を分割する `computed` で表現する**（決定#39）。
 *
 * **カルーセルとして動くのは2ページ以上ある4商品だけ**（`501` 6枚・`810` 6枚・`558` 5枚・`559` 5枚）。
 * 残る5商品は1ページなので**矢印とドットを出さない**。
 *
 * **支給 JS の高さ揃え（`equalizeProductReviewCards`）は移植しない**（決定#39）。
 * スライド内の同一行は支給CSS の `align-items: stretch` で揃い、スライド間は Swiper の
 * 非 `autoHeight` でコンテナ高さを共有する。2段ある場合の段どうしは `grid-auto-rows: 1fr` が要るが、
 * これは支給CSS に無いので**ページ組み立ての回に `vue-adjust.css` で足し、実機で確認する**。
 *
 * 矢印は支給HTMLには無く、slick が `prevArrow` / `nextArrow` オプションで生成している
 * （`script_main.js:331-335`）。同じクラス名の `<button>` を静的に置いて Swiper の
 * `navigation` に渡す。
 * → CONVERSION-GUIDE.md §3.3.1 の #12 ／ 決定#39
 */
import type SwiperClass from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

type ReviewCard = {
  title: string;
  text: string;
  /** 支給は `male` / `female` の2種類 */
  gender: 'male' | 'female';
  /** 「（男性・86歳）」 */
  meta: string;
};

type Props = {
  cards: ReviewCard[];
};

const props = defineProps<Props>();

const swiperEl = ref<HTMLElement | null>(null);
const paginationEl = ref<HTMLElement | null>(null);
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);
const perSlide = ref<number>(4);
let swiper: SwiperClass | null = null;

const slides = computed<ReviewCard[][]>(() => {
  const size = perSlide.value;
  return props.cards.reduce<ReviewCard[][]>((acc, card, index) => {
    if (index % size === 0) acc.push([]);
    acc[acc.length - 1].push(card);
    return acc;
  }, []);
});

let mediaQuery: MediaQueryList | null = null;
const applyPerSlide = (): void => {
  perSlide.value = mediaQuery?.matches ? 2 : 4;
};

onMounted(async () => {
  mediaQuery = window.matchMedia('(max-width: 768px)');
  applyPerSlide();
  mediaQuery.addEventListener('change', applyPerSlide);

  if (!swiperEl.value || slides.value.length <= 1) return;

  const [{ default: Swiper }, { A11y, Keyboard, Navigation, Pagination }] = await Promise.all([
    import('swiper'),
    import('swiper/modules'),
  ]);

  swiper = new Swiper(swiperEl.value, {
    modules: [A11y, Keyboard, Navigation, Pagination],
    slidesPerView: 1,
    loop: true,
    speed: 300,
    // 支給は `adaptiveHeight: false`。スライド間で高さを共有させる（決定#39）
    autoHeight: false,
    keyboard: { enabled: true },
    pagination: { el: paginationEl.value, clickable: true },
    navigation: { prevEl: prevEl.value, nextEl: nextEl.value },
  });
});

// カード数の割り方が変わったら Swiper に作り直させる（DOM の手組みはしない）
watch(slides, () => {
  swiper?.update();
});

onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', applyPerSlide);
  swiper?.destroy();
  swiper = null;
});
</script>

<style scoped>
/* 支給の `.slick-dots` に寄せる（`renewal/style.css:7176-7189`）。slick 本体のCSSは持ち込まないので、
   Swiper のページネーションを同じ見た目に揃える。

   **丸の実寸は slick 1.8.1 のフォントを実測して出した。** 支給は `li button:before` に
   `content: '•'` ＋ `font-family: 'slick'` を当てており、丸の大きさは font-size そのものではなく
   グリフの大きさで決まる。`slick.svg`（SVGフォント）の U+2022 は
   **units-per-em 512 に対して直径 438 の真円 ＝ 0.855em**。
   したがって支給の `font-size: 10px` は **直径 8.6px**。

   ⚠️ **`.product-review-slider .slick-dots …`（`:7176-7189`）の大きさと間隔は効いていない。**
   トップのFV用に先に書かれた `.slick-dots li { margin: 0 10px !important }`（`:1233`）と
   `.slick-dots li button:before { font-size: 9px !important }`（`:1236`）が `!important` で
   後勝ちを覆すため、**ブラウザが実際に描くのは 9px / margin 10px のほう**。
   支給HTMLの見え方に合わせるので、こちらの値を採る（→ CONVERSION-GUIDE §3.3.9）。

   ・大きさ … 9px × 0.855 ＝ 7.7px（`font-size: 10px` のほうは `!important` に負けて効かない）
   ・間隔 … `li { width: 20px（slick既定）; margin: 0 10px }` でピッチ 40px。
     丸 7.7px なので左右 16.15px でピッチ 40px に合わせる（丸の間の空きは 32.3px）
   ・位置 … `.slick-dots { bottom: -60px !important }`（`:1230`）が勝つので、ドットの帯は
     **枠の下端（`padding-bottom: 36px` の外側）から更に 60px 下**に出る。
     `ul` の高さ ＝ `li` の 20px で、グリフはその中央。したがって丸の下端は
     枠の下端から 60 − (10 − 3.85) ＝ **53.85px 下**（Swiper では負の `bottom`）
   ・色 … 非アクティブ #d0d0d0（不透明）、アクティブは
     `.slick-dots li.slick-active button:before { color: var(--smoky-red) !important }`（`:1239`）が
     `.product-review-slider …{ color: var(--green) }`（`:7187`）に勝つので **smoky-red**

   丸の下端を基準に置きたいので、帯そのものの高さを丸と同じにする（`font-size` / `line-height` を 0）。 */
.product-review-dots {
  --swiper-pagination-bullet-width: 7.7px;
  --swiper-pagination-bullet-height: 7.7px;
  --swiper-pagination-bullet-horizontal-gap: 16.15px;
  --swiper-pagination-bullet-inactive-color: #d0d0d0;
  --swiper-pagination-bullet-inactive-opacity: 1;
  --swiper-pagination-color: var(--smoky-red);
  --swiper-pagination-bottom: -53.85px;
  font-size: 0;
  line-height: 0;
}

/* SP は `.slick-dots { bottom: 0 !important }`（`:7579`）が同じ詳細度で後に来て勝つため枠の中に戻る。
   枠の下余白も `padding-bottom: 10vw` に変わる */
@media screen and (max-width: 768px) {
  .product-review-dots {
    --swiper-pagination-bottom: 6.15px;
  }
}
</style>
